import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth, OAuthProvider } from '../context/AuthContext';
import { brandHeroGradient } from '../theme/styleHelpers';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Divider,
  Alert,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Stack,
  InputAdornment,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Link as MuiLink,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Microsoft as MicrosoftIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';
import { authService } from '../services';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [showTwoFactorInput, setShowTwoFactorInput] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<OAuthProvider | null>(null);
  const [availableOAuthProviders, setAvailableOAuthProviders] = useState<OAuthProvider[]>([]);

  // Dual 2FA method states
  const [twoFactorMethod, setTwoFactorMethod] = useState<'totp' | 'otp'>('totp');
  const [otpChannel, setOtpChannel] = useState<'email' | 'sms'>('email');
  const [otpSent, setOtpSent] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [twoFactorUser, setTwoFactorUser] = useState<any>(null); // Store user data during 2FA flow

  const { login, verifyTwoFactor, loginWithOAuth, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { effectiveMode: mode } = useTheme();

  // Fetch available OAuth providers on mount
  useEffect(() => {
    const fetchOAuthProviders = async () => {
      try {
        const providers = await authService.getAvailableOAuthProviders();
        setAvailableOAuthProviders(providers);
      } catch (error) {
        console.error('Failed to fetch OAuth providers:', error);
        // Silently fail - OAuth buttons will be hidden
      }
    };
    fetchOAuthProviders();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !email || !password) return; // Prevent multiple submissions
    
    setError(null);
    setLoading(true);
    
    try {
      console.log('Login: Attempting login with credentials');
      const response = await login(email, password);
      
      if (response.requiresTwoFactor) {
        console.log('Login: Two-factor authentication required', response.user);
        setTwoFactorUser(response.user);
        setShowTwoFactorInput(true);

        // Set default method based on what's available
        if (response.user && response.user.verified_2fa_methods) {
          const methods = response.user.verified_2fa_methods;
          if (methods.includes('totp')) {
            setTwoFactorMethod('totp');
          } else if (methods.includes('email')) {
            setTwoFactorMethod('otp');
            setOtpChannel('email');
          } else if (methods.includes('sms')) {
            setTwoFactorMethod('otp');
            setOtpChannel('sms');
          }
        }
      } else {
        console.log('Login: Successful login, redirecting to dashboard');
        
        // Small delay to allow contexts to initialize properly after login
        // This helps prevent race conditions with the token being set but auth state not updated
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Check if there's a redirect URL in the query parameters
        const params = new URLSearchParams(window.location.search);
        const redirectTo = params.get('redirect');
        
        if (redirectTo) {
          console.log(`Login: Redirecting to ${decodeURIComponent(redirectTo)}`);
          navigate(decodeURIComponent(redirectTo));
        } else {
          console.log('Login: No redirect URL, going to dashboard');
          navigate('/dashboard');
        }
      }
    } catch (err: any) {
      console.error('Login: Login error:', err);
      
      // Provide more specific error messages based on the type of error
      if (err.code === 'CIRCUIT_OPEN' || err.status === 503) {
        setError('Service temporarily unavailable. Please try again in a few moments.');
      } else if (err.status === 401) {
        setError('Invalid email or password. Please try again.');
      } else if (err.message?.includes('timeout')) {
        setError('Request timed out. Please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtpCode = async () => {
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      console.log(`Login: Sending OTP code to ${otpChannel}`);
      // Use the unauthenticated login OTP endpoint that verifies credentials first
      const result = await authService.sendLoginOtp(email, password, otpChannel);
      setOtpSent(true);
      setSuccessMessage(result.message || `Verification code sent to your ${otpChannel}`);
    } catch (err: any) {
      console.error('Login: Failed to send OTP code:', err);
      setError(err.message || `Failed to send verification code to ${otpChannel}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !twoFactorCode) return; // Prevent multiple submissions

    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      if (twoFactorMethod === 'totp') {
        // TOTP verification - call login again with the 2FA token
        console.log('Login: Verifying TOTP authentication code');
        const response = await authService.login(email, password, twoFactorCode);

        // Login with 2FA token should complete the login and set tokens
        if (response && 'user' in response && response.user) {
          console.log('Login: TOTP verified successfully, login complete');
          // Manually refresh user in auth context since we bypassed the context's login method
          await refreshUser();
        } else {
          throw new Error('Invalid login response after 2FA verification');
        }
      } else {
        // OTP verification during login - uses unauthenticated endpoint
        console.log('Login: Verifying OTP authentication code');
        const result = await authService.verifyLoginOtp(email, password, twoFactorCode, otpChannel);

        // verifyLoginOtp returns full auth response with tokens already stored
        console.log('Login: OTP verified successfully, tokens stored');
        // Manually refresh user in auth context
        await refreshUser();
      }

      console.log('Login: Two-factor verification successful');

      // Check if there's a redirect URL in the query parameters
      const params = new URLSearchParams(window.location.search);
      const redirectTo = params.get('redirect');

      if (redirectTo) {
        console.log(`Login: Redirecting to ${decodeURIComponent(redirectTo)}`);
        navigate(decodeURIComponent(redirectTo));
      } else {
        console.log('Login: No redirect URL, going to dashboard');
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Login: Two-factor verification error:', err);
      setError(err.message || 'Invalid authentication code');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
      {/* Brand Logo/Header - Outside the dialog box */}
      <Box
        component={RouterLink}
        to="/"
        sx={{
          mt: { xs: 4, sm: 6 },
          mb: 4,
          textDecoration: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          '&:hover': {
            opacity: 0.8
          }
        }}
      >
        <Typography
          variant="h3"
          component="div"
          sx={(theme) => ({
            fontWeight: 800,
            background: brandHeroGradient(theme),
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5
          })}
        >
          Tier One Content
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.75rem' }}
        >
          Premium Content Sharing Platform
        </Typography>
      </Box>

      <Box display="flex" justifyContent="center">
        <Box width="100%">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 2,
              bgcolor: mode === 'dark' ? 'background.paper' : 'white'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" component="h1" gutterBottom>
                {showTwoFactorInput ? 'Two-Factor Authentication' : 'Login'}
              </Typography>

              <Typography variant="body2" color="text.secondary" align="center" mb={3}>
                {showTwoFactorInput
                  ? (twoFactorMethod === 'totp'
                      ? 'Please enter the verification code from your authenticator app'
                      : `We'll send a verification code to your ${otpChannel === 'email' ? 'email address' : 'phone number'}`)
                  : 'Sign in to access your account'
                }
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                  {error}
                </Alert>
              )}

              {successMessage && (
                <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
                  {successMessage}
                </Alert>
              )}

              {!showTwoFactorInput ? (
                <Box component="form" onSubmit={handleLogin} noValidate sx={{ width: '100%' }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={rememberMe} 
                          onChange={(e) => setRememberMe(e.target.checked)} 
                          color="primary" 
                        />
                      }
                      label="Remember me"
                    />
                    
                    <Button 
                      variant="text" 
                      component={RouterLink}
                      to="/forgot-password"
                      size="small"
                    >
                      Forgot password?
                    </Button>
                  </Box>
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    disabled={loading || !email || !password}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Sign In'}
                  </Button>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Typography variant="body2">
                      Don't have an account?{' '}
                      <MuiLink
                        component={RouterLink}
                        to="/register"
                        sx={{ fontWeight: 600 }}
                        color="primary"
                        underline="none"
                      >
                        Sign up
                      </MuiLink>
                    </Typography>
                  </Box>
                  
                  {availableOAuthProviders.length > 0 && (
                    <>
                      <Divider sx={{ my: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          OR
                        </Typography>
                      </Divider>

                      <Stack spacing={2}>
                        {availableOAuthProviders.includes('google') && (
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={oauthLoading === 'google' ? <CircularProgress size={20} /> : <GoogleIcon />}
                            onClick={() => {
                              setOauthLoading('google');
                              loginWithOAuth('google');
                            }}
                            disabled={!!oauthLoading}
                          >
                            Continue with Google
                          </Button>
                        )}

                        {availableOAuthProviders.includes('microsoft') && (
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={oauthLoading === 'microsoft' ? <CircularProgress size={20} /> : <MicrosoftIcon />}
                            onClick={() => {
                              setOauthLoading('microsoft');
                              loginWithOAuth('microsoft');
                            }}
                            disabled={!!oauthLoading}
                          >
                            Continue with Microsoft
                          </Button>
                        )}
                      </Stack>
                    </>
                  )}
                </Box>
              ) : (
                <Box component="form" onSubmit={handleTwoFactorVerification} noValidate sx={{ width: '100%' }}>
                  {/* Method Selection with Radio Buttons */}
                  <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                    <FormLabel component="legend" sx={{ mb: 2 }}>Choose verification method:</FormLabel>
                    <RadioGroup
                      value={twoFactorMethod === 'totp' ? 'totp' : otpChannel}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === 'totp') {
                          setTwoFactorMethod('totp');
                        } else {
                          setTwoFactorMethod('otp');
                          setOtpChannel(value as 'email' | 'sms');
                        }
                        setTwoFactorCode('');
                        setOtpSent(false);
                        setError(null);
                        setSuccessMessage(null);
                      }}
                    >
                      {/* Only show TOTP option if user has it verified */}
                      {twoFactorUser?.verified_2fa_methods?.includes('totp') && (
                        <FormControlLabel
                          value="totp"
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneAndroidIcon fontSize="small" />
                              <Typography>Authenticator App</Typography>
                            </Box>
                          }
                        />
                      )}
                      {/* Only show Email option if user has it verified */}
                      {twoFactorUser?.verified_2fa_methods?.includes('email') && (
                        <FormControlLabel
                          value="email"
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon fontSize="small" />
                              <Typography>Email Code</Typography>
                            </Box>
                          }
                        />
                      )}
                      {/* Only show SMS option if user has it verified */}
                      {twoFactorUser?.verified_2fa_methods?.includes('sms') && (
                        <FormControlLabel
                          value="sms"
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SmsIcon fontSize="small" />
                              <Typography>SMS Code</Typography>
                            </Box>
                          }
                        />
                      )}
                    </RadioGroup>
                  </FormControl>

                  {/* TOTP Flow - Just code input */}
                  {twoFactorMethod === 'totp' && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter the 6-digit code from your authenticator app or an 8-character backup code
                      </Typography>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="twoFactorCode"
                        label="Verification Code"
                        name="twoFactorCode"
                        autoFocus
                        value={twoFactorCode}
                        onChange={(e) => setTwoFactorCode(e.target.value)}
                        inputProps={{
                          pattern: "^[0-9]{6}$|^[a-f0-9]{8}$",
                          title: "Enter 6-digit code or 8-character backup code",
                          autoComplete: "off",
                          maxLength: 8,
                        }}
                      />
                    </>
                  )}

                  {/* OTP Flow - Send code then enter code */}
                  {twoFactorMethod === 'otp' && (
                    <>
                      {!otpSent ? (
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handleSendOtpCode}
                          disabled={loading}
                          sx={{ mt: 3, mb: 2 }}
                        >
                          {loading ? <CircularProgress size={24} /> : `Send Code to ${otpChannel === 'email' ? 'Email' : 'SMS'}`}
                        </Button>
                      ) : (
                        <>
                          <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="twoFactorCode"
                            label="Verification Code"
                            name="twoFactorCode"
                            autoFocus
                            value={twoFactorCode}
                            onChange={(e) => setTwoFactorCode(e.target.value)}
                            inputProps={{
                              pattern: "^[0-9]{6}$",
                              title: "Enter 6-digit code",
                              autoComplete: "off",
                              maxLength: 6,
                            }}
                            helperText="Enter the 6-digit code sent to your device"
                          />
                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={loading || !twoFactorCode}
                          >
                            {loading ? <CircularProgress size={24} /> : 'Verify'}
                          </Button>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleSendOtpCode}
                            disabled={loading}
                            sx={{ mb: 2 }}
                          >
                            Resend Code
                          </Button>
                        </>
                      )}
                    </>
                  )}

                  {/* TOTP Flow - Show verify button */}
                  {twoFactorMethod === 'totp' && (
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                      disabled={loading || !twoFactorCode}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Verify'}
                    </Button>
                  )}

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setShowTwoFactorInput(false);
                      setTwoFactorCode('');
                      setOtpSent(false);
                      setError(null);
                      setSuccessMessage(null);
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default Login;